import { useEffect, useRef, useState } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNetworkStatus } from '@shared/hooks/useNetworkStatus';
import { cn } from '@core/utils/cn';

/** Fixed banner: persistent while offline, brief confirmation when back online. */
export function OfflineBanner(): React.JSX.Element | null {
  const { t } = useTranslation();
  const { isOnline } = useNetworkStatus();
  const [showRestored, setShowRestored] = useState(false);
  const wasOffline = useRef(false);

  useEffect(() => {
    if (!isOnline) {
      wasOffline.current = true;
      return;
    }
    if (wasOffline.current) {
      wasOffline.current = false;
      setShowRestored(true);
      const timer = setTimeout(() => setShowRestored(false), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOnline]);

  if (isOnline && !showRestored) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-2 px-4 py-2.5 text-center text-sm font-medium',
        isOnline ? 'bg-teal text-teal-ink' : 'bg-danger text-danger-ink',
      )}
    >
      {isOnline ? <Wifi size={16} aria-hidden="true" /> : <WifiOff size={16} aria-hidden="true" />}
      {isOnline ? t('states.backOnline') : t('states.offlineBanner')}
    </div>
  );
}
